import React, { useState } from 'react';

interface Props {
  /** The key, shown once. Held by the caller's state only; never logged or stored. */
  recoveryKey: string;
  /** The button under the box, which the box must be ticked to use. */
  buttonLabel: string;
  onSaved: () => void;
}

/**
 * A new recovery key as it is shown once: the key, Copy and Download, and the box to
 * tick before leaving. The Account page's dialog shows every key with it, the first
 * one too (#922, #1105).
 */
const RecoveryKeyDisplay: React.FC<Props> = ({ recoveryKey, buttonLabel, onSaved }) => {
  const [confirmed, setConfirmed] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(recoveryKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const content = `Chaptery Recovery Key\n\n${recoveryKey}\n\nStore this key somewhere safe. It can only be used to recover your account if you lose access to your email.`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'chaptery-recovery-key.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="recovery-key-setup">
      <div className="recovery-key-display">
        <code className="recovery-key-text">
          {/* A line may break after each hyphen, so a narrow screen wraps the key between
              its groups, not inside one. <wbr> adds no text, so a selection copies the key
              as it is. */}
          {recoveryKey.split('-').map((group, i) => (
            <React.Fragment key={i}>{i > 0 && <>-<wbr /></>}{group}</React.Fragment>
          ))}
        </code>
      </div>

      <div className="recovery-key-actions">
        <button className="btn-secondary" onClick={handleCopy}>
          {copied ? 'Copied!' : 'Copy'}
        </button>
        <button className="btn-secondary" onClick={handleDownload}>
          Download as .txt
        </button>
      </div>

      <label className="recovery-confirm-label">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={e => setConfirmed(e.target.checked)}
        />
        <span>I've saved my recovery key in a safe place.</span>
      </label>

      <button
        className="btn-primary"
        disabled={!confirmed}
        onClick={onSaved}
        style={{ width: '100%', marginTop: '1rem' }}
      >
        {buttonLabel}
      </button>
    </div>
  );
};

export default RecoveryKeyDisplay;
